import React from "react";
import { Box, Typography, Stack, Chip } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CellTowerIcon from "@mui/icons-material/CellTower";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ContactUs from "../Modals/ContactUs";
import AlertCard from "../AlertCard";

interface STRMonitoringProps {
  title: string;
  subTitle: string;
  buttonText?: string;
  buttonLink?: string;
  backgroundSize?: any;
  minHeight?: string;
  backgroundPosition?: string;
  backgroundImage?: string;
  button?: boolean;
  showRadar?: boolean;
  chipLabel?: string;
  ctaLabel?: string;
  defaultMessage?: string;
  ctaHelperText?: string;
  analyticsSource?: string;
  audience?: string;
}

const Hero: React.FC<STRMonitoringProps> = ({
  title,
  subTitle,
  minHeight,
  button = true,
  showRadar = true,
  chipLabel = "Built for cities, HOAs, and local governments",
  ctaLabel,
  defaultMessage,
  ctaHelperText = "Need STR visibility for your city, HOA, or jurisdiction? Tell us about your area and we'll help you get started.",
  analyticsSource = "hero",
  audience,
}) => {
  return (
    <Box
      component="section"
      id="home"
      sx={{
        position: "relative",
        pt: { xs: 8, lg: 12 },
        pb: { xs: 12, lg: 16 },
        minHeight: minHeight ?? "auto",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "25%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 700,
          height: 350,
          background:
            "linear-gradient(135deg, rgba(124, 58, 237, 0.2), rgba(79, 70, 229, 0.15), rgba(59, 130, 246, 0.05))",
          filter: "blur(140px)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(#1e293b 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          opacity: 0.25,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "7fr 5fr" },
            gap: { xs: 6, lg: 4 },
            alignItems: "center",
          }}
        >
          <Stack
            spacing={4}
            sx={{ textAlign: { xs: "center", lg: "left" }, alignItems: { xs: "center", lg: "flex-start" } }}
          >
            <Chip
              label={chipLabel}
              sx={{
                bgcolor: "rgba(139, 92, 246, 0.1)",
                border: "1px solid rgba(139, 92, 246, 0.25)",
                color: "#d8b4fe",
                fontWeight: 500,
                height: "auto",
                py: 0.75,
                px: 0.5,
                "& .MuiChip-label": { px: 1.5, whiteSpace: "normal" },
              }}
            />

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.25rem", sm: "3.5rem", lg: "3.75rem" },
                fontWeight: 800,
                color: "#fff",
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
              }}
            >
              {title.includes("Short-Term Rentals") ? (
                <>
                  {title.split("Short-Term Rentals")[0]}
                  <Box
                    component="span"
                    sx={{
                      background:
                        "linear-gradient(90deg, #a78bfa, #a5b4fc, #f472b6)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Short-Term Rentals
                  </Box>
                  {title.split("Short-Term Rentals")[1]}
                </>
              ) : (
                title
              )}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: "#cbd5e1",
                lineHeight: 1.7,
                maxWidth: 640,
              }}
            >
              {subTitle}
            </Typography>

            {button && (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 480,
                  bgcolor: "rgba(15, 23, 42, 0.9)",
                  p: 2.5,
                  borderRadius: "16px",
                  border: "1px solid rgba(30, 41, 59, 1)",
                  boxShadow: "0 25px 50px rgba(0,0,0,0.35)",
                }}
              >
                <Typography
                  sx={{ mb: 2, color: "#94a3b8", fontSize: "0.875rem" }}
                >
                  {ctaHelperText}
                </Typography>
                <ContactUs
                  buttonLabel={ctaLabel}
                  buttonLabelMobile={ctaLabel}
                  defaultMessage={defaultMessage}
                  dialogTitle={ctaLabel || "Contact Us for Information"}
                  analyticsContext={{
                    source: analyticsSource,
                    ...(audience ? { audience } : {}),
                  }}
                />
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "center", sm: "flex-start" }}
                  spacing={{ xs: 0.75, sm: 0 }}
                  sx={{
                    mt: 2,
                    color: "#94a3b8",
                    fontSize: "0.75rem",
                    textAlign: { xs: "center", sm: "left" },
                  }}
                >
                  <Box component="span">100% Confidential</Box>
                  <Box component="span">Typically reply within 1 business day</Box>
                </Stack>
              </Box>
            )}

            <Box
              sx={{
                pt: 3,
                borderTop: "1px solid rgba(30, 41, 59, 0.8)",
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: { xs: 1.5, sm: 3 },
                width: "100%",
                maxWidth: 480,
                textAlign: "left",
              }}
            >
              {[
                { value: "Find", label: "Listings" },
                { value: "Ongoing", label: "Monitoring", accent: true },
                { value: "Compliance", label: "Support" },
              ].map((stat) => (
                <Box key={stat.label}>
                  <Typography
                    sx={{
                      fontSize: { xs: "1.1rem", sm: "1.875rem" },
                      fontWeight: 800,
                      color: stat.accent ? "#a78bfa" : "#fff",
                      lineHeight: 1.2,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      color: "#94a3b8",
                      mt: 0.5,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>

          {showRadar && (
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: -8,
                  background: "linear-gradient(90deg, #7c3aed, #db2777)",
                  borderRadius: "24px",
                  filter: "blur(40px)",
                  opacity: 0.25,
                  animation: "radar-pulse 3s ease-in-out infinite",
                  pointerEvents: "none",
                }}
              />
              <Box
                sx={{
                  position: "relative",
                  bgcolor: "rgba(15, 23, 42, 0.9)",
                  border: "1px solid rgba(30, 41, 59, 1)",
                  borderRadius: "24px",
                  p: 3,
                  boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
                  backdropFilter: "blur(20px)",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ pb: 2, borderBottom: "1px solid rgba(30, 41, 59, 1)" }}
                >
                  <Stack direction="row" spacing={1}>
                    {["#f43f5e", "#f59e0b", "#10b981"].map((c) => (
                      <Box
                        key={c}
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: c,
                        }}
                      />
                    ))}
                  </Stack>
                  <Chip
                    size="small"
                    label="LIVE MONITORING"
                    icon={
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#34d399",
                          ml: 1,
                          animation: "live-dot 1.5s ease-in-out infinite",
                        }}
                      />
                    }
                    sx={{
                      bgcolor: "rgba(139, 92, 246, 0.1)",
                      border: "1px solid rgba(139, 92, 246, 0.2)",
                      color: "#a78bfa",
                      fontFamily: "monospace",
                      fontSize: "0.65rem",
                      height: 28,
                      "& .MuiChip-icon": { marginLeft: "8px" },
                    }}
                  />
                </Stack>

                <Stack spacing={2} sx={{ mt: 2.5 }}>
                  <AlertCard
                    accent="#f43f5e"
                    icon={<WarningAmberIcon fontSize="small" />}
                    title="142 Oak Street"
                    badge="Airbnb Match"
                    detail={'Listed as "Modern Downtown Loft" • $380/night'}
                    flag="HOA Bylaw: No STRs < 30 Days"
                    time="8m ago"
                  />
                  <AlertCard
                    accent="#f59e0b"
                    icon={<CellTowerIcon fontSize="small" />}
                    title="318 Maple Ave"
                    badge="VRBO Match"
                    detail="Possible unpermitted short-term rental"
                    flag="Permit check recommended"
                    time="1h ago"
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      bgcolor: "rgba(2, 6, 23, 0.5)",
                      border: "1px solid rgba(30, 41, 59, 0.8)",
                      fontSize: "0.75rem",
                      color: "#cbd5e1",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <PictureAsPdfIcon sx={{ color: "#a78bfa", fontSize: 16, flexShrink: 0 }} />
                      <Box
                        component="span"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: { xs: "normal", sm: "nowrap" },
                        }}
                      >
                        Report ready for compliance review
                      </Box>
                    </Stack>
                    <Typography
                      sx={{
                        color: "#a78bfa",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        flexShrink: 0,
                      }}
                    >
                      Preview →
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Hero;
